import React, { useState } from 'react';
import { X, Hash, Lock, Search, UserPlus } from 'lucide-react';

interface Channel {
  id: string;
  name: string;
  description?: string;
  type: 'public' | 'private' | 'direct';
  members: string[];
}

interface ChannelBrowserModalProps {
  show: boolean;
  channels: Channel[];
  currentUserId: string;
  onClose: () => void;
  onJoinChannel: (channelId: string) => Promise<void>;
}

const ChannelBrowserModal: React.FC<ChannelBrowserModalProps> = ({
  show,
  channels,
  currentUserId,
  onClose,
  onJoinChannel
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isJoining, setIsJoining] = useState(false);

  if (!show) return null;

  // 현재 사용자가 참여하지 않은 public 채널만 표시
  const availableChannels = channels.filter(
    channel =>
      channel.type === 'public' &&
      !channel.members.includes(currentUserId)
  );

  // 검색 필터링
  const filteredChannels = availableChannels.filter(channel =>
    channel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (channel.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleJoin = async (channelId: string) => {
    setIsJoining(true);
    try {
      await onJoinChannel(channelId);
      // 성공 시 모달 닫기
      onClose();
    } catch (error) {
      console.error('Failed to join channel:', error);
      alert('채널 참여에 실패했습니다.');
    } finally {
      setIsJoining(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              채널 둘러보기
            </h2>
            <p className="text-sm text-gray-500">
              참여할 수 있는 공개 채널을 찾아보세요
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="채널 이름 또는 설명으로 검색..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
          </div>
        </div>

        {/* Channels List */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredChannels.length === 0 ? (
            <div className="text-center py-12">
              <Hash className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">
                {searchTerm
                  ? '검색 결과가 없습니다.'
                  : '참여할 수 있는 공개 채널이 없습니다.'}
              </p>
              {availableChannels.length === 0 && !searchTerm && (
                <p className="text-sm text-gray-400 mt-2">
                  모든 공개 채널에 이미 참여했습니다!
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredChannels.map(channel => (
                <div
                  key={channel.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all"
                >
                  <div className="flex items-start gap-3 flex-1">
                    <div className="mt-1">
                      {channel.type === 'private' ? (
                        <Lock className="w-5 h-5 text-gray-400" />
                      ) : (
                        <Hash className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">
                          {channel.name}
                        </h3>
                        <span className="text-xs text-gray-500">
                          {channel.members.length}명
                        </span>
                      </div>
                      {channel.description && (
                        <p className="text-sm text-gray-600 mt-1">
                          {channel.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleJoin(channel.id)}
                    disabled={isJoining}
                    className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2 flex-shrink-0"
                  >
                    <UserPlus className="w-4 h-4" />
                    참여하기
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChannelBrowserModal;
